/**
 * Client OpenID Connect (transport pur, aucune notion d'app ni de Vue ici).
 *
 * Flux **Authorization Code + PKCE** contre le fournisseur d'Olympe :
 *
 * 1. `fetchDiscovery` lit `<issuer>/.well-known/openid-configuration` — les
 *    points de terminaison ne sont pas codés en dur ;
 * 2. `createAuthorizationRequest` fabrique `state`, `nonce`, `code_verifier` et
 *    l'URL de la page de login (`authorization_endpoint`, chez Olympe
 *    `https://account.olympe.xyz/login`), que l'app ouvre dans le navigateur ;
 * 3. la page revient sur l'URI de redirection avec `?code=…&state=…` ;
 * 4. `exchangeCode` échange le code au `token_endpoint` avec le `code_verifier` ;
 * 5. `verifyIdToken` valide l'`id_token` (signature RS256 via `jwks_uri`,
 *    émetteur, audience, expiration, nonce) et `fetchUserInfo` complète le profil.
 *
 * Olympe ne délivre pas de `refresh_token` (`grant_types_supported` ne liste que
 * `authorization_code`) : une session expirée = on recommence au 2.
 *
 * Le SHA-256 (PKCE) et la vérification de signature passent par WebCrypto,
 * donc exigent un contexte sécurisé (`https://`, `capacitor://`,
 * `http://localhost`) : le live reload sur l'IP du LAN n'est pas pris en charge.
 */

export interface DiscoveryDocument {
  issuer: string
  authorization_endpoint: string
  token_endpoint: string
  userinfo_endpoint: string
  jwks_uri: string
}

export interface TokenResponse {
  access_token: string
  id_token: string
  token_type: string
  /** durée de vie en secondes ; absent chez certains fournisseurs */
  expires_in: number | null
  scope: string | null
}

/** Revendications de l'`id_token` qu'on exploite (cf. `claims_supported`). */
export interface IdTokenClaims {
  iss: string
  sub: string
  aud: string | string[]
  exp: number
  iat: number
  nonce?: string
  preferred_username?: string
  email?: string
  [claim: string]: unknown
}

/** Réponse du `userinfo_endpoint`, réduite à ce qu'on affiche. */
export interface UserInfo {
  sub: string
  preferred_username: string | null
  email: string | null
  name: string | null
  picture: string | null
}

export interface AuthorizationRequest {
  url: string
  state: string
  nonce: string
  codeVerifier: string
  redirectUri: string
}

export type OidcErrorCode =
  | 'discovery'
  | 'network'
  | 'invalid_client'
  | 'invalid_grant'
  | 'invalid_token'
  | 'state_mismatch'
  | 'denied'
  | 'insecure_context'
  | 'unknown'

export class OidcError extends Error {
  readonly code: OidcErrorCode

  constructor(code: OidcErrorCode, message: string) {
    super(message)
    this.name = 'OidcError'
    this.code = code
  }
}

// --- Encodages ---------------------------------------------------------------

const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function base64UrlDecode(text: string): Uint8Array {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(text.length / 4) * 4, '=')
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function decodeJsonSegment<T>(segment: string): T {
  return JSON.parse(new TextDecoder().decode(base64UrlDecode(segment))) as T
}

/** Chaîne aléatoire sur l'alphabet base64url — `crypto.getRandomValues` existe partout. */
export function randomString(length = 43): string {
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  let out = ''
  for (const byte of bytes) out += BASE64URL[byte % 64]
  return out
}

// --- WebCrypto ---------------------------------------------------------------

/**
 * `crypto.subtle` n'existe qu'en contexte sécurisé (`https://`, `capacitor://`,
 * `http://localhost`) : c'est le cas partout où l'app tourne, sauf en live
 * reload sur l'IP du LAN — non pris en charge, volontairement.
 */
function subtle(): SubtleCrypto {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new OidcError('insecure_context', 'WebCrypto indisponible : contexte non sécurisé')
  }
  return crypto.subtle
}

export async function sha256(text: string): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', new TextEncoder().encode(text)))
}

// --- Découverte --------------------------------------------------------------

const discoveryCache = new Map<string, Promise<DiscoveryDocument>>()

/** Lit (et garde en mémoire) le document de découverte de l'émetteur. */
export function fetchDiscovery(issuer: string): Promise<DiscoveryDocument> {
  const cached = discoveryCache.get(issuer)
  if (cached) return cached

  const request = fetch(`${issuer.replace(/\/$/, '')}/.well-known/openid-configuration`)
    .then(async (response) => {
      if (!response.ok) throw new OidcError('discovery', `découverte OIDC : HTTP ${response.status}`)
      const document = (await response.json()) as Partial<DiscoveryDocument>
      for (const key of [
        'issuer',
        'authorization_endpoint',
        'token_endpoint',
        'userinfo_endpoint',
        'jwks_uri',
      ] as const) {
        if (typeof document[key] !== 'string') {
          throw new OidcError('discovery', `découverte OIDC : champ « ${key} » manquant`)
        }
      }
      return document as DiscoveryDocument
    })
    .catch((reason: unknown) => {
      // une découverte ratée doit pouvoir être retentée
      discoveryCache.delete(issuer)
      if (reason instanceof OidcError) throw reason
      throw new OidcError('network', `découverte OIDC injoignable : ${String(reason)}`)
    })

  discoveryCache.set(issuer, request)
  return request
}

// --- Autorisation ------------------------------------------------------------

export interface AuthorizationOptions {
  issuer: string
  clientId: string
  scopes: string[]
  redirectUri: string
}

/** Génère les secrets d'une tentative de connexion et l'URL de la page de login. */
export async function createAuthorizationRequest(
  options: AuthorizationOptions,
): Promise<AuthorizationRequest> {
  const discovery = await fetchDiscovery(options.issuer)
  const state = randomString(32)
  const nonce = randomString(32)
  const codeVerifier = randomString(64)
  const codeChallenge = base64UrlEncode(await sha256(codeVerifier))

  const url = new URL(discovery.authorization_endpoint)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', options.clientId)
  url.searchParams.set('redirect_uri', options.redirectUri)
  url.searchParams.set('scope', options.scopes.join(' '))
  url.searchParams.set('state', state)
  url.searchParams.set('nonce', nonce)
  url.searchParams.set('code_challenge', codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')

  return { url: url.toString(), state, nonce, codeVerifier, redirectUri: options.redirectUri }
}

/** Le retour du fournisseur : `code` + `state`, ou `error` (refus, client inconnu…). */
export interface AuthorizationResponse {
  code: string | null
  state: string | null
  error: string | null
  errorDescription: string | null
}

/**
 * Lit la réponse d'autorisation dans une URL de retour. Les paramètres peuvent
 * être dans la query (`app://cb?code=…`) ou, en navigateur avec un routeur en
 * mode hash, après le `#` (`http://…/#/profile?code=…`) : on regarde les deux.
 */
export function parseAuthorizationResponse(url: string): AuthorizationResponse | null {
  let params: URLSearchParams | null = null

  try {
    const parsed = new URL(url)
    params = parsed.searchParams
    if (!params.has('code') && !params.has('error')) {
      const hashQuery = parsed.hash.indexOf('?')
      params = hashQuery === -1 ? null : new URLSearchParams(parsed.hash.slice(hashQuery + 1))
    }
  } catch {
    return null
  }

  if (!params || (!params.has('code') && !params.has('error'))) return null

  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  }
}

// --- Jetons ------------------------------------------------------------------

export interface ExchangeOptions {
  issuer: string
  clientId: string
  clientSecret: string | null
  code: string
  codeVerifier: string
  redirectUri: string
}

/** Échange le code d'autorisation contre les jetons (`client_secret_post` si un secret est fourni). */
export async function exchangeCode(options: ExchangeOptions): Promise<TokenResponse> {
  const discovery = await fetchDiscovery(options.issuer)

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code: options.code,
    client_id: options.clientId,
    redirect_uri: options.redirectUri,
    code_verifier: options.codeVerifier,
  })
  if (options.clientSecret) body.set('client_secret', options.clientSecret)

  let response: Response
  try {
    response = await fetch(discovery.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body,
    })
  } catch (reason) {
    throw new OidcError('network', `token_endpoint injoignable : ${String(reason)}`)
  }

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>

  if (!response.ok) {
    const error = typeof payload.error === 'string' ? payload.error : 'unknown'
    const description =
      typeof payload.error_description === 'string' ? payload.error_description : `HTTP ${response.status}`
    const code: OidcErrorCode =
      error === 'invalid_client' ? 'invalid_client' : error === 'invalid_grant' ? 'invalid_grant' : 'unknown'
    throw new OidcError(code, `${error} : ${description}`)
  }

  if (typeof payload.access_token !== 'string' || typeof payload.id_token !== 'string') {
    throw new OidcError('invalid_token', 'réponse du token_endpoint sans access_token / id_token')
  }

  return {
    access_token: payload.access_token,
    id_token: payload.id_token,
    token_type: typeof payload.token_type === 'string' ? payload.token_type : 'Bearer',
    expires_in: typeof payload.expires_in === 'number' ? payload.expires_in : null,
    scope: typeof payload.scope === 'string' ? payload.scope : null,
  }
}

export interface VerifyOptions {
  issuer: string
  clientId: string
  nonce: string
  /** tolérance d'horloge en secondes */
  leeway?: number
}

interface JsonWebKey_ {
  kid?: string
  kty: string
  alg?: string
  use?: string
  n?: string
  e?: string
}

async function fetchJwks(jwksUri: string): Promise<JsonWebKey_[]> {
  const response = await fetch(jwksUri)
  if (!response.ok) throw new OidcError('invalid_token', `jwks_uri : HTTP ${response.status}`)
  const payload = (await response.json()) as { keys?: JsonWebKey_[] }
  return Array.isArray(payload.keys) ? payload.keys : []
}

/**
 * Vérifie la signature RS256 d'un JWT avec la clé du JWKS dont le `kid`
 * correspond. Rend `false` si aucune clé ne correspond ou si la signature est
 * fausse.
 */
async function verifySignature(
  token: string,
  header: { alg?: string; kid?: string },
  jwksUri: string,
): Promise<boolean> {
  const api = subtle()
  if (header.alg !== 'RS256') return false

  const keys = await fetchJwks(jwksUri)
  const candidates = keys.filter(
    (key) => key.kty === 'RSA' && (header.kid === undefined || key.kid === header.kid),
  )

  const [headerPart, payloadPart, signaturePart] = token.split('.')
  if (!headerPart || !payloadPart || !signaturePart) return false
  const data = new TextEncoder().encode(`${headerPart}.${payloadPart}`)
  const signature = base64UrlDecode(signaturePart)

  for (const jwk of candidates) {
    try {
      const key = await api.importKey(
        'jwk',
        { kty: 'RSA', n: jwk.n, e: jwk.e, alg: 'RS256', ext: true },
        { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
        false,
        ['verify'],
      )
      if (await api.verify('RSASSA-PKCS1-v1_5', key, signature, data)) return true
    } catch {
      // clé inexploitable : on essaie la suivante
    }
  }
  return false
}

/** Décode un JWT sans le vérifier (pour lire `exp` d'un jeton déjà validé, par exemple). */
export function decodeJwt<T = Record<string, unknown>>(token: string): T | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    return decodeJsonSegment<T>(parts[1]!)
  } catch {
    return null
  }
}

/** Valide l'`id_token` (signature, émetteur, audience, expiration, nonce) et rend ses revendications. */
export async function verifyIdToken(idToken: string, options: VerifyOptions): Promise<IdTokenClaims> {
  const parts = idToken.split('.')
  if (parts.length !== 3) throw new OidcError('invalid_token', 'id_token mal formé')

  let header: { alg?: string; kid?: string }
  let claims: IdTokenClaims
  try {
    header = decodeJsonSegment<{ alg?: string; kid?: string }>(parts[0]!)
    claims = decodeJsonSegment<IdTokenClaims>(parts[1]!)
  } catch {
    throw new OidcError('invalid_token', 'id_token illisible')
  }

  const discovery = await fetchDiscovery(options.issuer)
  const leeway = options.leeway ?? 120
  const now = Math.floor(Date.now() / 1000)

  if (claims.iss !== discovery.issuer) {
    throw new OidcError('invalid_token', `id_token : émetteur inattendu « ${claims.iss} »`)
  }
  const audiences = Array.isArray(claims.aud) ? claims.aud : [claims.aud]
  if (!audiences.includes(options.clientId)) {
    throw new OidcError('invalid_token', 'id_token : audience inattendue')
  }
  if (typeof claims.exp !== 'number' || claims.exp + leeway < now) {
    throw new OidcError('invalid_token', 'id_token expiré')
  }
  if (typeof claims.iat === 'number' && claims.iat - leeway > now) {
    throw new OidcError('invalid_token', 'id_token émis dans le futur')
  }
  if (claims.nonce !== options.nonce) {
    throw new OidcError('invalid_token', 'id_token : nonce inattendu')
  }
  if (typeof claims.sub !== 'string' || !claims.sub) {
    throw new OidcError('invalid_token', 'id_token sans sujet')
  }

  if (!(await verifySignature(idToken, header, discovery.jwks_uri))) {
    throw new OidcError('invalid_token', 'id_token : signature invalide')
  }

  return claims
}

// --- Profil ------------------------------------------------------------------

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

/** Lit le profil au `userinfo_endpoint` avec le jeton d'accès. */
export async function fetchUserInfo(issuer: string, accessToken: string): Promise<UserInfo> {
  const discovery = await fetchDiscovery(issuer)

  let response: Response
  try {
    response = await fetch(discovery.userinfo_endpoint, {
      headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
    })
  } catch (reason) {
    throw new OidcError('network', `userinfo_endpoint injoignable : ${String(reason)}`)
  }

  if (response.status === 401) throw new OidcError('invalid_token', 'jeton d’accès refusé par userinfo')
  if (!response.ok) throw new OidcError('unknown', `userinfo : HTTP ${response.status}`)

  const payload = (await response.json()) as Record<string, unknown>
  if (typeof payload.sub !== 'string') throw new OidcError('invalid_token', 'userinfo sans sujet')

  return {
    sub: payload.sub,
    preferred_username: optionalString(payload.preferred_username),
    email: optionalString(payload.email),
    name: optionalString(payload.name),
    picture: optionalString(payload.picture),
  }
}
